import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative rounded-lg overflow-hidden bg-background-100 cursor-pointer"
      >
        <div className="aspect-[16/9] lg:aspect-[21/9] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            title={`${article.title} - 封面图片`}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-white mb-2">
            {article.category}
          </span>
          <h3 className="font-heading text-lg md:text-2xl font-bold text-white line-clamp-2 mb-2 group-hover:text-primary-300 transition-colors duration-200">
            {article.title}
          </h3>
          <p className="text-sm text-white/70 line-clamp-2 hidden sm:block">{article.excerpt}</p>
          <div className="flex items-center mt-3 text-xs text-white/60 space-x-4">
            <span className="flex items-center">
              <i className="ri-time-line mr-1"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center">
              <i className="ri-eye-line mr-1"></i>
              {article.viewCount.toLocaleString()}
            </span>
            <span className="flex items-center">
              <i className="ri-star-fill text-accent-400 mr-1"></i>
              {article.rating}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
      >
        <div className="w-full sm:w-44 h-28 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            title={`${article.title} - 封面图片`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-700 mb-1.5">
            {article.category}
          </span>
          <h3 className="font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200 text-sm">
            {article.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1.5 line-clamp-1">{article.excerpt}</p>
          <div className="flex items-center mt-2 text-xs text-foreground-400 space-x-3">
            <span>{article.publishDate}</span>
            <span>{article.readTime}阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-white border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={`${article.title} - 封面图片`}
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-700">
            {article.category}
          </span>
          <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
        </div>
        <h3 className="font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200 text-sm">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2">{article.excerpt}</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-100">
          <div className="flex items-center text-xs text-foreground-400">
            <span>{article.publishDate}</span>
          </div>
          <div className="flex items-center text-xs text-foreground-400">
            <i className="ri-eye-line mr-1"></i>
            <span>{article.viewCount.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}